"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";

import { normalizeMedNameForLearning } from "@/lib/learnedCorrections";

import { PharmacyWarehouseBackdrop } from "@/components/PharmacyWarehouseBackdrop";
import { compressImageBase64ForApi } from "@/lib/compressImageForApi";
import { getBatchScanUxProfile } from "@/lib/batchScanDeviceProfile";
import {
  BATCH_SCAN_MONTH_NAMES,
  buildBatchScanLineDisplay,
  formatBatchScanExpiryLabel,
} from "@/lib/batchScanDisplay";
import { fetchWithRetry } from "@/lib/anthropicRetry";
import {
  batchScanExitIsRisky,
  clearBatchScanRecovery,
  saveBatchScanRecovery,
} from "@/lib/batchScanRecovery";
import {
  POS_REFERENCE_EVENT,
  filterPosReferenceSuggestions,
  loadPosReference,
} from "@/lib/posReferenceIndex";
import { humanizeNetworkOrUnknown } from "@/lib/humanizeClientError";
import {
  detectBarcodesFromFile,
  isBarcodeDetectorSupported,
  lookupProductByBarcode,
} from "@/lib/barcodeScanner";

const font =
  'var(--font-geist-sans), system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const STEP_CAPTURE = "capture";
const STEP_DATE = "date";
const STEP_CONFIRM = "confirm";
const LONG_PRESS_MS = 400;

/** Label parse lifecycle: enhancement only — never blocks transitions (`ready` = success or network failure, structured empty). */
const PARSE_IDLE = "idle";
const PARSE_LOADING = "loading";
const PARSE_READY = "ready";

/** Recovery payloads may carry legacy `ok`/`error` or corrupted values — coerce to the live parse state machine. */
function coerceRecoveryApiStatus(raw) {
  if (raw === "ok" || raw === "error") return PARSE_READY;
  if (raw === PARSE_IDLE || raw === PARSE_LOADING || raw === PARSE_READY) return raw;
  return PARSE_IDLE;
}

/** Stable identity for month wheel columns (avoids needless child resets). */
const MONTH_WHEEL_VALUES = BATCH_SCAN_MONTH_NAMES.map((_, i) => i + 1);

function nowParts() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

/** Default expiry: current month, current calendar year + 1 (spec). */
function defaultExpiryParts() {
  const n = nowParts();
  return { month: n.month, year: n.year + 1 };
}

function clampFuture(month, year) {
  const n = nowParts();
  if (year < n.year) return { month: n.month, year: n.year };
  if (year === n.year && month < n.month) return { month: n.month, year: n.year };
  return { month, year };
}

function isIdleBatchRecovery(step, labelBase64, confirmedCount) {
  return step === STEP_CAPTURE && !labelBase64 && confirmedCount === 0;
}

/** Date/confirm work in progress — includes barcode flow (no label photo) and text-only recovery. */
function draftHasUnconfirmedWork(step, draft) {
  if (step !== STEP_DATE && step !== STEP_CONFIRM) return false;
  return (
    Boolean(draft.labelBase64) ||
    Boolean(String(draft.name || "").trim()) ||
    draft.apiStatus === PARSE_LOADING
  );
}

function readViewportWidth() {
  if (typeof window === "undefined") return 390;
  const vv = window.visualViewport;
  return Math.round(vv?.width ?? window.innerWidth) || 390;
}

function hapticLight() {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(12);
  } catch {
    /* ignore */
  }
}

function resolveQuantity(digits) {
  if (digits === null || digits === "") return 1;
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(999, n);
}

function displayQuantityString(digits) {
  if (digits === null) return "1";
  if (digits === "") return "";
  return digits;
}

function normalizeUnitForSave(unitType) {
  const u = String(unitType || "unit").trim().toLowerCase();
  return u || "unit";
}

/** Map POS / sheet unit strings into batch draft `unitType` tokens. */
function mapPosRefUnitToDraftUnit(u) {
  const s = String(u || "").toLowerCase();
  if (s.includes("tablet")) return "tablets";
  if (s.includes("ml") || /\bm\s*l\b/.test(s)) return "mL";
  if (s.includes("box")) return "boxes";
  return String(u || "").trim();
}

function emptyDraft() {
  const exp = defaultExpiryParts();
  const cf = clampFuture(exp.month, exp.year);
  return {
    labelBase64: null,
    labelMediaType: "image/jpeg",
    month: cf.month,
    year: cf.year,
    apiStatus: PARSE_IDLE,
    name: "",
    strength: "",
    dosageForm: "",
    unitType: "",
    attentionName: false,
    attentionStrength: false,
    attentionForm: false,
    unit: "unit",
    matchStatus: "not_checked",
    suggestedName: null,
    /** Raw product name from last label AI parse (for global learning when user edits before save). */
    aiParsedName: "",
  };
}

function fieldPointerHandlers(active, startLongPress, clearLongPress, openFn) {
  if (!active) return {};
  return {
    onPointerDown: () => startLongPress(openFn),
    onPointerUp: clearLongPress,
    onPointerCancel: clearLongPress,
    onPointerLeave: clearLongPress,
  };
}

/**
 * Tap-only month/year grid — replaces the old scroll wheels. Split screen: 12 big
 * month cells on the left, a handful of upcoming years on the right. Every cell is a
 * single tap; there is nothing to scroll or fling.
 */
function MonthYearGridPicker({ month, year, years, onPickMonth, onPickYear, compact = false }) {
  const yearRows = Math.max(1, Math.ceil(years.length / 2));
  return (
    <div style={{ display: "flex", gap: compact ? 8 : 14, flex: compact ? "0 0 auto" : 1, minHeight: 0 }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div
          className="ed-batch-grid-heading"
          style={{
            fontSize: compact ? "0.68rem" : "0.78rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            color: "rgba(245,245,247,0.9)",
            marginBottom: compact ? 6 : 10,
          }}
        >
          Month
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gridAutoRows: compact ? 36 : undefined,
            gridTemplateRows: compact ? undefined : "repeat(4, 1fr)",
            gap: compact ? 6 : 9,
            flex: compact ? undefined : 1,
            minHeight: 0,
          }}
        >
          {MONTH_WHEEL_VALUES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                hapticLight();
                onPickMonth(m);
              }}
              className={m === month ? "ed-batch-grid-cell ed-batch-grid-cell--selected" : "ed-batch-grid-cell"}
              style={{ fontSize: compact ? "0.8rem" : "1.08rem", flexDirection: "column", gap: 1 }}
            >
              <span>{BATCH_SCAN_MONTH_NAMES[m - 1].slice(0, 3).toUpperCase()}</span>
              {/* Month number (Jan=1 … Dec=12) — a quick, language-agnostic cross-check
                  next to the abbreviation so the right cell is easy to spot at a glance. */}
              <span className="ed-batch-grid-cell-sub">{m}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="ed-batch-grid-divider" />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div
          className="ed-batch-grid-heading"
          style={{
            fontSize: compact ? "0.68rem" : "0.78rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            color: "rgba(245,245,247,0.9)",
            marginBottom: compact ? 6 : 10,
          }}
        >
          Year
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gridAutoRows: compact ? 36 : undefined,
            gridTemplateRows: compact ? undefined : `repeat(${yearRows}, 1fr)`,
            gap: compact ? 6 : 9,
            flex: compact ? undefined : 1,
            minHeight: 0,
          }}
        >
          {years.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => {
                hapticLight();
                onPickYear(y);
              }}
              className={y === year ? "ed-batch-grid-cell ed-batch-grid-cell--selected" : "ed-batch-grid-cell"}
              style={{ fontSize: compact ? "0.84rem" : "1.14rem" }}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SkeletonBlock({ h = 14 }) {
  return (
    <div
      className="ed-batch-skeleton"
      aria-hidden="true"
      style={{
        height: h,
        borderRadius: 8,
        background: "linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.14), rgba(255,255,255,0.06))",
        backgroundSize: "200% 100%",
        animation: "ed-batch-skel 1.1s ease-in-out infinite",
        marginBottom: 8,
      }}
    />
  );
}

const NumericKeypad = memo(function NumericKeypad({ onDigit, onClear, onBackspace, disabled = false }) {
  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
  ];
  return (
    <div style={{ display: "grid", gap: 10, paddingTop: 4 }}>
      {keys.map((row) => (
        <div key={row.join("")} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {row.map((d) => (
            <button
              key={d}
              type="button"
              disabled={disabled}
              className="ed-keypad-cell"
              onClick={() => {
                hapticLight();
                onDigit(d);
              }}
              style={{
                height: 52,
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.06)",
                color: "#f5f5f7",
                fontSize: "1.35rem",
                fontWeight: 600,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.45 : 1,
              }}
            >
              {d}
            </button>
          ))}
        </div>
      ))}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <button
          type="button"
          disabled={disabled}
          className="ed-keypad-cell"
          onClick={() => {
            hapticLight();
            onClear();
          }}
          style={{
            height: 52,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.04)",
            color: "rgba(235,235,245,0.85)",
            fontSize: "0.9rem",
            fontWeight: 700,
          }}
        >
          Clear
        </button>
        <button
          type="button"
          disabled={disabled}
          className="ed-keypad-cell"
          onClick={() => {
            hapticLight();
            onDigit("0");
          }}
          style={{
            height: 52,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "#f5f5f7",
            fontSize: "1.35rem",
            fontWeight: 600,
          }}
        >
          0
        </button>
        <button
          type="button"
          disabled={disabled}
          className="ed-keypad-cell"
          onClick={() => {
            hapticLight();
            onBackspace();
          }}
          style={{
            height: 52,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.04)",
            color: "rgba(235,235,245,0.85)",
            fontSize: "1.1rem",
            fontWeight: 700,
          }}
          aria-label="Backspace"
        >
          ⌫
        </button>
      </div>
    </div>
  );
});
NumericKeypad.displayName = "NumericKeypad";

export function BatchScanView({
  onSaveItem,
  onUndoItem: _onUndoItem,
  onDone,
  onCancel,
  parseAccessToken,
  userId = null,
  recoveryInitial = null,
  onRecoveryConsumed,
  onExitRiskChange,
}) {
  void _onUndoItem;
  const router = useRouter();
  const mountedRef = useRef(true);
  const claudeAbortRef = useRef(/** @type {AbortController | null} */ (null));
  const recoveryFlushRef = useRef(() => {});
  const kbInsetRafRef = useRef(/** @type {number | null} */ (null));
  const longPressTimerRef = useRef(/** @type {number | null} */ (null));
  /** User-edited label fields — late Claude responses must not overwrite these. */
  const labelFieldEditedRef = useRef({ name: false, strength: false, form: false });
  const proceedLockRef = useRef(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const barcodeFileRef = useRef(null);
  const nameInputRef = useRef(null);
  const strengthInputRef = useRef(null);
  const formInputRef = useRef(null);

  const [step, setStep] = useState(STEP_CAPTURE);
  const [sessionUi, setSessionUi] = useState("scanner");
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [flash, setFlash] = useState(false);
  const [saveError, setSaveError] = useState(null);
  /** id of the item the active `saveError` banner can retry/skip; null once resolved. */
  const [saveErrorItemId, setSaveErrorItemId] = useState(null);
  /** Items that failed to save and were skipped — surfaced again on the summary screen. */
  const [failedItems, setFailedItems] = useState([]);
  /** Map key = payload.id, value = { payload, line, attempts } — lets "Retry" resend the same item. */
  const failedPayloadsRef = useRef(new Map());
  /** Shown when label AI request fails after retries (user can still type manually). */
  const [aiParseWarn, setAiParseWarn] = useState("");
  const [itemIndex, setItemIndex] = useState(1);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [sessionLog, setSessionLog] = useState([]);
  /** Mirrors `sessionLog` for synchronous recovery writes after confirm (state updates are async). */
  const sessionLogRef = useRef([]);
  /** Map key = payload.id while `onSaveItem` has not completed successfully. */
  const outboundPendingRef = useRef(new Map());
  const [viewportW, setViewportW] = useState(readViewportWidth);
  const [pipActive, setPipActive] = useState(false);
  const wakeLockRef = useRef(null);
  const lastSavedRef = useRef(null);
  const recoveryAppliedRef = useRef(false);
  /**
   * Number of synthetic browser-history entries we've pushed on top of the base
   * `/upload` entry, one per advanced wizard step. This lets the device/OS/gesture
   * Back button (and the in-app ← Back) walk back through the capture → date → confirm
   * steps instead of tearing down the whole scanner and dumping the user on the dashboard.
   */
  const historyDepthRef = useRef(0);
  /** popstate events fired by our own programmatic `history.go()` that must be ignored. */
  const suppressPopRef = useRef(0);
  /** Mirror of `step`, read synchronously inside the (stable) popstate listener. */
  const stepRef = useRef(STEP_CAPTURE);

  const [draft, setDraft] = useState(() => emptyDraft());
  const [quantityDigits, setQuantityDigits] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [checkFlash, setCheckFlash] = useState(false);
  const [deferSaveToast, setDeferSaveToast] = useState(false);
  const [kbInset, setKbInset] = useState(0);
  const [barcodeSupported] = useState(() => isBarcodeDetectorSupported());
  /** Local index from POS / stock-sheet uploads — offline autocomplete for typed names. */
  const [posReferenceEntries, setPosReferenceEntries] = useState([]);
  /**
   * Whether the user has tapped a month/year cell on the current visit to the date
   * step. Tapping either side is enough to set it; tapping the SECOND side auto-advances
   * to the quantity step. Reset every time the date step is (re-)entered so returning
   * to fix one field always requires re-confirming both, keeping the rule simple.
   */
  const [monthPicked, setMonthPicked] = useState(false);
  const [yearPicked, setYearPicked] = useState(false);
  /**
   * Whether the keypad is mid-entry (vs. the quantity having last been set by tapping
   * the +1 display or freshly reset). The keypad's first digit press always REPLACES
   * the value unless this is true — that's what keeps "tap number = +1" and "type an
   * exact amount" from bleeding into each other (e.g. tap to 4, then type "1" "0" must
   * become 10, not 41).
   */
  const quantityTypingRef = useRef(false);

  const n = nowParts();
  const years = useMemo(() => Array.from({ length: 8 }, (_, i) => n.year + i), [n.year]);
  const uxProfile = useMemo(() => getBatchScanUxProfile(viewportW), [viewportW]);

  const hasActiveUnconfirmed = draftHasUnconfirmedWork(step, draft);

  useEffect(() => {
    sessionLogRef.current = sessionLog;
  }, [sessionLog]);

  useEffect(() => {
    if (!userId) {
      setPosReferenceEntries([]);
      return;
    }
    let cancelled = false;
    void loadPosReference(userId).then((rows) => {
      if (!cancelled) setPosReferenceEntries(rows);
    });
    const onRef = () => {
      void loadPosReference(userId).then((rows) => {
        if (!cancelled) setPosReferenceEntries(rows);
      });
    };
    window.addEventListener(POS_REFERENCE_EVENT, onRef);
    return () => {
      cancelled = true;
      window.removeEventListener(POS_REFERENCE_EVENT, onRef);
    };
  }, [userId]);

  const posNameSuggestions = useMemo(
    () => filterPosReferenceSuggestions(posReferenceEntries, draft.name, 12),
    [posReferenceEntries, draft.name],
  );

  useEffect(() => {
    if (!recoveryInitial || recoveryAppliedRef.current) return;
    if (!userId || recoveryInitial.userId !== userId) return;
    recoveryAppliedRef.current = true;
    setStep(recoveryInitial.step);
    setItemIndex(recoveryInitial.itemIndex);
    const slog = recoveryInitial.sessionLog ?? [];
    setSessionLog(slog);
    sessionLogRef.current = slog;
    setConfirmedCount(slog.length > 0 ? slog.length : recoveryInitial.confirmedCount);
    outboundPendingRef.current = new Map();
    for (const row of recoveryInitial.outboundPending ?? []) {
      if (row && typeof row.key === "number" && row.item) {
        outboundPendingRef.current.set(row.key, row.item);
      }
    }
    const d = recoveryInitial.draft;
    setDraft({
      labelBase64: d.labelBase64,
      labelMediaType: d.labelMediaType || "image/jpeg",
      month: d.month,
      year: d.year,
      apiStatus: coerceRecoveryApiStatus(d.apiStatus),
      name: d.name || "",
      strength: typeof d.strength === "string" ? d.strength : d.dosage || "",
      dosageForm: d.dosageForm || "",
      unitType: d.unitType || "",
      attentionName: Boolean(d.attentionName),
      attentionStrength: Boolean(d.attentionStrength),
      attentionForm: Boolean(d.attentionForm),
      unit: d.unit || "unit",
      matchStatus: d.matchStatus || "not_checked",
      suggestedName: d.suggestedName ?? null,
      aiParsedName: typeof d.aiParsedName === "string" ? d.aiParsedName : "",
    });
    const qb = d.quantityBuffer;
    const restoredDigits = qb === undefined || qb === null ? null : qb;
    setQuantityDigits(restoredDigits);
    // A non-empty restored buffer was mid-typed when the session was saved — keep
    // appending to it; an empty/absent one behaves like a fresh quantity step.
    quantityTypingRef.current = Boolean(restoredDigits);
    setSaveError(null);
    onRecoveryConsumed?.();

    const toReplay = recoveryInitial.outboundPending ?? [];
    if (toReplay.length) {
      queueMicrotask(() => {
        for (const row of toReplay) {
          const item = row.item;
          const key = row.key;
          if (!item || typeof key !== "number") continue;
          void (async () => {
            try {
              const saved = await onSaveItem?.(item);
              if (!mountedRef.current) return;
              if (saved === false || saved == null) throw new Error("Could not save item");
              if (saved && saved._batchSaveDeferred) {
                setDeferSaveToast(true);
                window.setTimeout(() => {
                  if (mountedRef.current) setDeferSaveToast(false);
                }, 4200);
              }
              outboundPendingRef.current.delete(key);
              window.setTimeout(() => recoveryFlushRef.current(), 0);
            } catch (e) {
              if (!mountedRef.current) return;
              outboundPendingRef.current.delete(key);
              setSaveError(humanizeNetworkOrUnknown(e));
              setSaveErrorItemId(key);
              failedPayloadsRef.current.set(key, { payload: item, line: item.name, attempts: 1 });
              setFailedItems((prev) => [...prev, { id: key, line: item.name }]);
              setSessionLog((prev) => {
                const next = prev.filter((row) => row.key !== key);
                sessionLogRef.current = next;
                return next;
              });
              setConfirmedCount((c) => Math.max(0, c - 1));
              window.setTimeout(() => recoveryFlushRef.current(), 0);
            }
          })();
        }
      });
    }
  }, [recoveryInitial, userId, onRecoveryConsumed, onSaveItem]);

  useEffect(() => {
    onExitRiskChange?.(
      batchScanExitIsRisky({
        confirmedCount,
        hasActiveUnconfirmed,
      }),
    );
  }, [confirmedCount, hasActiveUnconfirmed, onExitRiskChange]);

  useEffect(() => {
    if (draft.labelBase64) setSaveError(null);
  }, [draft.labelBase64]);

  const recoveryPayload = useMemo(
    () => ({
      v: 1,
      userId: userId || "",
      savedAt: Date.now(),
      step,
      itemIndex,
      confirmedCount,
      draft: {
        labelBase64: draft.labelBase64,
        labelMediaType: draft.labelMediaType,
        month: draft.month,
        year: draft.year,
        apiStatus: draft.apiStatus,
        name: draft.name,
        strength: draft.strength,
        dosageForm: draft.dosageForm,
        unitType: draft.unitType,
        quantity: resolveQuantity(quantityDigits),
        quantityBuffer: quantityDigits === null ? null : quantityDigits,
        attentionName: draft.attentionName,
        attentionStrength: draft.attentionStrength,
        attentionForm: draft.attentionForm,
        unit: draft.unit,
        matchStatus: draft.matchStatus,
        suggestedName: draft.suggestedName,
        aiParsedName: draft.aiParsedName,
      },
    }),
    [userId, step, itemIndex, confirmedCount, draft, quantityDigits],
  );

  const flushRecoveryToStorage = useCallback(() => {
    if (!userId) return;
    const log = sessionLogRef.current;
    const outboundPending = Array.from(outboundPendingRef.current.entries()).map(([key, item]) => ({
      key,
      item,
    }));
    const idle =
      isIdleBatchRecovery(step, draft.labelBase64, confirmedCount) &&
      log.length === 0 &&
      outboundPending.length === 0;
    if (idle) {
      void clearBatchScanRecovery(userId);
      return;
    }
    void saveBatchScanRecovery({
      ...recoveryPayload,
      userId,
      savedAt: Date.now(),
      sessionLog: log,
      outboundPending,
    });
  }, [userId, step, confirmedCount, recoveryPayload, draft.labelBase64]);

  recoveryFlushRef.current = flushRecoveryToStorage;

  useEffect(() => {
    if (!userId) return;
    const log = sessionLogRef.current;
    const outboundPending = Array.from(outboundPendingRef.current.entries()).map(([key, item]) => ({
      key,
      item,
    }));
    const idle =
      isIdleBatchRecovery(step, draft.labelBase64, confirmedCount) &&
      log.length === 0 &&
      outboundPending.length === 0;
    if (idle) {
      void clearBatchScanRecovery(userId);
      return;
    }
    const payload = {
      ...recoveryPayload,
      userId,
      savedAt: Date.now(),
      sessionLog: log,
      outboundPending,
    };
    const ms = uxProfile.recoverySaveDebounceMs;
    const t = window.setTimeout(() => {
      void saveBatchScanRecovery(payload);
    }, ms);
    return () => window.clearTimeout(t);
  }, [
    userId,
    step,
    itemIndex,
    confirmedCount,
    recoveryPayload,
    uxProfile.recoverySaveDebounceMs,
    draft.labelBase64,
    sessionLog,
  ]);

  const releaseWakeLock = useCallback(() => {
    const w = wakeLockRef.current;
    wakeLockRef.current = null;
    void w?.release?.();
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.wakeLock?.request) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request("screen");
      wakeLockRef.current.addEventListener("release", () => {
        wakeLockRef.current = null;
      });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!cameraReady || !uxProfile.allowWakeLock) return;
    void requestWakeLock();
    return () => {
      releaseWakeLock();
    };
  }, [cameraReady, uxProfile.allowWakeLock, requestWakeLock, releaseWakeLock]);

  /** Stable listeners: always read latest flush from recoveryFlushRef (avoids re-subscribing every draft edit). */
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        recoveryFlushRef.current();
        return;
      }
      if (cameraReady && uxProfile.allowWakeLock && !wakeLockRef.current) {
        void requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    const onPageHide = () => recoveryFlushRef.current();
    if (uxProfile.flushOnPageHide) {
      window.addEventListener("pagehide", onPageHide, true);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (uxProfile.flushOnPageHide) {
        window.removeEventListener("pagehide", onPageHide, true);
      }
    };
  }, [uxProfile.flushOnPageHide, uxProfile.allowWakeLock, cameraReady, requestWakeLock]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !cameraReady || !uxProfile.allowPictureInPicture) return;
    const onEnter = () => setPipActive(true);
    const onLeave = () => setPipActive(false);
    v.addEventListener("enterpictureinpicture", onEnter);
    v.addEventListener("leavepictureinpicture", onLeave);
    return () => {
      v.removeEventListener("enterpictureinpicture", onEnter);
      v.removeEventListener("leavepictureinpicture", onLeave);
    };
  }, [cameraReady, uxProfile.allowPictureInPicture]);

  const togglePictureInPicture = useCallback(async () => {
    const v = videoRef.current;
    if (!v || !uxProfile.allowPictureInPicture) return;
    try {
      if (document.pictureInPictureElement === v) {
        await document.exitPictureInPicture();
        return;
      }
      await v.requestPictureInPicture();
    } catch (e) {
      console.warn("Picture-in-Picture:", e);
    }
  }, [uxProfile.allowPictureInPicture]);

  useEffect(() => {
    const apply = () => setViewportW(readViewportWidth());
    apply();
    window.addEventListener("resize", apply, { passive: true });
    window.addEventListener("orientationchange", apply, { passive: true });
    const vv = window.visualViewport;
    const updateKbInset = () => {
      const h = window.innerHeight;
      const vvh = vv?.height ?? h;
      setKbInset(Math.max(0, h - vvh - (vv?.offsetTop ?? 0)));
    };
    const onVv = () => {
      apply();
      if (kbInsetRafRef.current != null) cancelAnimationFrame(kbInsetRafRef.current);
      kbInsetRafRef.current = requestAnimationFrame(() => {
        kbInsetRafRef.current = null;
        updateKbInset();
      });
    };
    vv?.addEventListener("resize", onVv, { passive: true });
    vv?.addEventListener("scroll", onVv, { passive: true });
    onVv();
    return () => {
      if (kbInsetRafRef.current != null) cancelAnimationFrame(kbInsetRafRef.current);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      vv?.removeEventListener("resize", onVv);
      vv?.removeEventListener("scroll", onVv);
    };
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCameraReady(false);
    setCameraError(false);
    (async () => {
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) setCameraError(true);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          await v.play().catch(() => {});
        }
        if (!cancelled) setCameraReady(true);
      } catch {
        if (!cancelled) setCameraError(true);
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [stopStream]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      claudeAbortRef.current?.abort();
      claudeAbortRef.current = null;
      if (longPressTimerRef.current != null) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
  }, []);

  const captureFromVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return null;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return null;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.88);
    const comma = dataUrl.indexOf(",");
    const meta = dataUrl.slice(0, comma);
    return {
      base64: dataUrl.slice(comma + 1),
      mediaType: /data:([^;]+)/.exec(meta)?.[1] || "image/jpeg",
    };
  }, []);

  /** Shared path for label-parse failures — never let the promise reject uncaught (avoids native error dialogs). */
  const reportBatchParseFailure = useCallback((e, signal) => {
    if (signal.aborted) return;
    if (e instanceof DOMException && e.name === "AbortError") return;
    if (e instanceof Error && e.name === "AbortError") return;
    if (!mountedRef.current) return;
    setAiParseWarn(humanizeNetworkOrUnknown(e));
    setDraft((d) => {
      const ed = labelFieldEditedRef.current;
      return {
        ...d,
        apiStatus: PARSE_READY,
        name: ed.name ? d.name : "",
        strength: ed.strength ? d.strength : "",
        dosageForm: ed.form ? d.dosageForm : "",
        unitType: ed.form ? d.unitType : "",
        attentionName: ed.name ? d.attentionName : true,
        attentionStrength: ed.strength ? d.attentionStrength : true,
        attentionForm: ed.form ? d.attentionForm : true,
      };
    });
  }, []);

  const runClaude = useCallback(
    async (labelBase64, labelMediaType, month, year, signal) => {
      try {
        let b64 = labelBase64;
        let mt = labelMediaType;
        try {
          const compressed = await compressImageBase64ForApi(labelBase64, labelMediaType);
          if (!signal.aborted) {
            b64 = compressed.base64;
            mt = compressed.mediaType;
          }
        } catch (e) {
          console.warn("compressImageBase64ForApi skipped:", e);
        }

        const headers = { "Content-Type": "application/json" };
        if (parseAccessToken) headers.Authorization = `Bearer ${parseAccessToken}`;
        const res = await fetchWithRetry(
          "/api/parse-batch-scan",
          {
            method: "POST",
            headers,
            signal,
            body: JSON.stringify({
              labelExtractV2: true,
              pairs: [{ labelBase64: b64, labelMediaType: mt, expiryMonth: month, expiryYear: year }],
            }),
          },
          { retries: 2, delayMs: 2000, label: "parse-batch-scan" },
        );
        if (signal.aborted) return;
        let data = {};
        try {
          data = await res.json();
        } catch {
          throw new Error("Something didn't work as expected. Try again.");
        }
        if (!res.ok) {
          const rawErr = data.message ?? data.error ?? "Scan didn't complete. Please try again.";
          const errMsg = typeof rawErr === "string" ? rawErr : "Scan didn't complete. Please try again.";
          throw new Error(errMsg);
        }
        const med = data.meds?.[0];
        if (!med) throw new Error("We couldn't read this item. Try again.");
        const name = typeof med.name === "string" ? med.name : "";
        const strength = typeof med.strength === "string" ? med.strength : "";
        const dosageForm = typeof med.dosageForm === "string" ? med.dosageForm : "";
        const unitType = typeof med.unitType === "string" ? med.unitType : "";
        if (!mountedRef.current || signal.aborted) return;
        setAiParseWarn("");
        setDraft((d) => {
          const ed = labelFieldEditedRef.current;
          const nextName = ed.name ? d.name : name;
          const nextStrength = ed.strength ? d.strength : strength;
          const nextDosageForm = ed.form ? d.dosageForm : dosageForm;
          const nextUnitType = ed.form ? d.unitType : unitType;
          return {
            ...d,
            apiStatus: PARSE_READY,
            name: nextName,
            strength: nextStrength,
            dosageForm: nextDosageForm,
            unitType: nextUnitType,
            aiParsedName: name,
            attentionName: ed.name ? d.attentionName : nextName.trim() === "",
            attentionStrength: ed.strength ? d.attentionStrength : nextStrength.trim() === "",
            attentionForm: ed.form
              ? d.attentionForm
              : nextDosageForm.trim() === "" || nextUnitType.trim() === "",
          };
        });
      } catch (e) {
        reportBatchParseFailure(e, signal);
      }
    },
    [parseAccessToken, reportBatchParseFailure],
  );

  const startItemFromShot = useCallback(
    (shot) => {
      if (!shot) return;
      const exp = defaultExpiryParts();
      const cf = clampFuture(exp.month, exp.year);
      claudeAbortRef.current?.abort();
      const ac = new AbortController();
      claudeAbortRef.current = ac;
      labelFieldEditedRef.current = { name: false, strength: false, form: false };
      setFlash(true);
      window.setTimeout(() => setFlash(false), 70);
      setDraft({
        ...emptyDraft(),
        labelBase64: shot.base64,
        labelMediaType: shot.mediaType,
        month: cf.month,
        year: cf.year,
        apiStatus: PARSE_LOADING,
      });
      setSaveError(null);
      setAiParseWarn("");
      setStep(STEP_DATE);
      queueMicrotask(() => {
        void runClaude(shot.base64, shot.mediaType, cf.month, cf.year, ac.signal).catch((e) =>
          reportBatchParseFailure(e, ac.signal),
        );
      });
    },
    [runClaude, reportBatchParseFailure],
  );

  const handleCapture = useCallback(() => {
    if (step !== STEP_CAPTURE) return;
    if (!cameraReady) return;
    const shot = captureFromVideo();
    if (shot) startItemFromShot(shot);
  }, [step, cameraReady, captureFromVideo, startItemFromShot]);

  const onPickFile = useCallback(
    (e) => {
      if (step !== STEP_CAPTURE) return;
      const f = e.target.files?.[0];
      e.target.value = "";
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl !== "string") return;
        const comma = dataUrl.indexOf(",");
        const meta = dataUrl.slice(0, comma);
        startItemFromShot({
          base64: dataUrl.slice(comma + 1),
          mediaType: f.type || /data:([^;]+)/.exec(meta)?.[1] || "image/jpeg",
        });
      };
      reader.readAsDataURL(f);
    },
    [step, startItemFromShot],
  );

  const onPickBarcodeFile = useCallback(
    async (e) => {
      if (step !== STEP_CAPTURE) return;
      const f = e.target.files?.[0];
      e.target.value = "";
      if (!f || !barcodeSupported) return;
      const codes = await detectBarcodesFromFile(f);
      if (!codes.length) {
        setAiParseWarn("No barcode detected. Try a clearer photo.");
        return;
      }
      const lookup = await lookupProductByBarcode(codes[0].rawValue);
      const exp = defaultExpiryParts();
      const cf = clampFuture(exp.month, exp.year);
      setDraft({
        ...emptyDraft(),
        month: cf.month,
        year: cf.year,
        apiStatus: PARSE_READY,
        name: lookup?.name || `Barcode ${codes[0].rawValue}`,
        strength: lookup?.strength || "",
        dosageForm: "",
        unitType: "unit",
        attentionName: false,
        attentionStrength: true,
        attentionForm: true,
      });
      setAiParseWarn("");
      setStep(STEP_DATE);
    },
    [step, barcodeSupported],
  );

  useEffect(() => {
    if (step !== STEP_DATE && step !== STEP_CONFIRM) return;
    const { month, year } = clampFuture(draft.month, draft.year);
    if (month !== draft.month || year !== draft.year) {
      setDraft((d) => ({ ...d, month, year }));
    }
  }, [step, draft.month, draft.year]);

  const setMonth = useCallback((month) => {
    setDraft((d) => {
      const next = clampFuture(month, d.year);
      return { ...d, month: next.month, year: next.year };
    });
  }, []);

  const setYear = useCallback((year) => {
    setDraft((d) => {
      const next = clampFuture(d.month, year);
      return { ...d, month: next.month, year: next.year };
    });
  }, []);

  const goToStep3 = useCallback(() => {
    setStep(STEP_CONFIRM);
    setQuantityDigits(null);
    quantityTypingRef.current = false;
  }, []);

  /** Reset the tap-tracking every time the date step is (re-)entered — forward or via Back. */
  useEffect(() => {
    if (step === STEP_DATE) {
      setMonthPicked(false);
      setYearPicked(false);
    }
  }, [step]);

  /** Month/year tap handlers used by the STEP_DATE grid: tapping the second side auto-advances. */
  const pickMonth = useCallback(
    (month) => {
      setMonth(month);
      setMonthPicked(true);
    },
    [setMonth],
  );

  const pickYear = useCallback(
    (year) => {
      setYear(year);
      setYearPicked(true);
    },
    [setYear],
  );

  useEffect(() => {
    if (step !== STEP_DATE) return;
    if (!monthPicked || !yearPicked) return;
    // Small delay so the second tap's selected state is visible before the screen slides.
    const t = window.setTimeout(() => {
      goToStep3();
    }, 220);
    return () => window.clearTimeout(t);
  }, [step, monthPicked, yearPicked, goToStep3]);

  /** Step back: confirm (quantity) → date (expiry). Keeps the in-progress draft. */
  const stepBackToDate = useCallback(() => {
    setStep(STEP_DATE);
  }, []);

  /** Step back: date (expiry) → capture (camera). Discards the current shot so the user re-shoots. */
  const stepBackToCapture = useCallback(() => {
    claudeAbortRef.current?.abort();
    claudeAbortRef.current = null;
    setDraft(emptyDraft());
    setQuantityDigits(null);
    quantityTypingRef.current = false;
    setEditingField(null);
    setSaveError(null);
    setAiParseWarn("");
    setStep(STEP_CAPTURE);
  }, []);

  /**
   * In-app ← Back. Routed through browser history so it behaves identically to the
   * hardware/gesture Back button: one wizard step at a time, only leaving the scanner
   * for the dashboard from the first (capture) step.
   */
  const goBackStep = useCallback(() => {
    if (historyDepthRef.current > 0) {
      window.history.back();
      return;
    }
    router.push("/dashboard");
  }, [router]);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  /**
   * Keep the browser history stack in sync with the current wizard step so Back
   * traverses steps. Each scanner step maps to a synthetic history depth
   * (capture = 0, date = 1, confirm = 2). Moving deeper pushes entries; moving
   * shallower (e.g. "Send to dashboard" starting a new item, or opening the session summary)
   * pops them. `history.go()` fires a single popstate we must ignore.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const target = sessionUi === "scanner" ? (step === STEP_CONFIRM ? 2 : step === STEP_DATE ? 1 : 0) : 0;
    const current = historyDepthRef.current;
    if (target > current) {
      for (let depth = current; depth < target; depth += 1) {
        // Preserve Next.js' own history state on the synthetic entry; otherwise the
        // App Router treats a popstate landing on it as a foreign entry and does a
        // hard reconstruct (splash/loading flash) instead of a soft in-page change.
        const nextState = { ...(window.history.state || {}), edBatchScanDepth: depth + 1 };
        window.history.pushState(nextState, "");
      }
      historyDepthRef.current = target;
    } else if (target < current) {
      const diff = current - target;
      historyDepthRef.current = target;
      suppressPopRef.current += 1;
      window.history.go(-diff);
    }
  }, [step, sessionUi]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => {
      if (suppressPopRef.current > 0) {
        suppressPopRef.current -= 1;
        return;
      }
      if (historyDepthRef.current <= 0) {
        // Base `/upload` entry popped: the user is genuinely leaving the scanner;
        // let the route change proceed (the app shell closes batch scan).
        return;
      }
      historyDepthRef.current -= 1;
      if (stepRef.current === STEP_CONFIRM) {
        stepBackToDate();
      } else if (stepRef.current === STEP_DATE) {
        stepBackToCapture();
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [stepBackToDate, stepBackToCapture]);

  const applyPosReferencePick = useCallback((entry) => {
    labelFieldEditedRef.current.name = true;
    labelFieldEditedRef.current.strength = true;
    labelFieldEditedRef.current.form = true;
    const ut = mapPosRefUnitToDraftUnit(entry.unit);
    setDraft((d) => ({
      ...d,
      name: entry.name,
      strength: entry.dosage ? entry.dosage : d.strength,
      unitType: ut || d.unitType,
      attentionName: false,
      attentionStrength: !String(entry.dosage || "").trim(),
      attentionForm: !String(ut || "").trim(),
    }));
  }, []);

  const confirmItem = useCallback(() => {
    if (step !== STEP_CONFIRM || proceedLockRef.current) return;
    if (!draft.labelBase64 && !String(draft.name || "").trim()) return;
    proceedLockRef.current = true;
    const qty = resolveQuantity(quantityDigits);
    const displayLine = buildBatchScanLineDisplay({
      quantity: qty,
      unitType: draft.unitType || "unit",
      name: draft.name,
      strength: draft.strength,
      dosageForm: draft.dosageForm,
      expiryMonth: draft.month,
      expiryYear: draft.year,
    });
    const aiName = typeof draft.aiParsedName === "string" ? draft.aiParsedName.trim() : "";
    const finalName = typeof draft.name === "string" ? draft.name.trim() : "";
    const productNameWasCorrected =
      Boolean(aiName && finalName) &&
      normalizeMedNameForLearning(aiName) !== normalizeMedNameForLearning(finalName);

    const payload = {
      id: Date.now(),
      name: displayLine,
      dosage: "",
      quantity: qty,
      unit: normalizeUnitForSave(draft.unitType),
      expiryMonth: draft.month,
      expiryYear: draft.year,
      pricePerBox: null,
      status: null,
      matchStatus: "not_checked",
      suggestedName: null,
      /** Raw label product name when user fixed OCR before save (dashboard banner + learning). */
      originalName: productNameWasCorrected ? aiName : null,
    };

    if (parseAccessToken && productNameWasCorrected) {
      void fetch("/api/learning/name-correction", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${parseAccessToken}`,
        },
        body: JSON.stringify({ wrongName: aiName, correctName: finalName }),
        keepalive: true,
      }).catch(() => {});
    }

    setSaveError(null);
    lastSavedRef.current = { id: payload.id, name: displayLine };
    const nextLog = [...sessionLogRef.current, { key: payload.id, line: displayLine, quantity: qty }];
    sessionLogRef.current = nextLog;
    outboundPendingRef.current.set(payload.id, { ...payload });

    setConfirmedCount((c) => c + 1);
    setSessionLog(nextLog);
    setItemIndex((x) => x + 1);
    setCheckFlash(true);
    window.setTimeout(() => {
      if (mountedRef.current) setCheckFlash(false);
    }, 300);
    const cleared = emptyDraft();
    setDraft(cleared);
    setQuantityDigits(null);
    quantityTypingRef.current = false;
    setEditingField(null);
    setStep(STEP_CAPTURE);
    proceedLockRef.current = false;

    if (userId) {
      const outboundPending = Array.from(outboundPendingRef.current.entries()).map(([key, item]) => ({
        key,
        item,
      }));
      void saveBatchScanRecovery({
        v: 1,
        userId,
        savedAt: Date.now(),
        step: STEP_CAPTURE,
        itemIndex: itemIndex + 1,
        confirmedCount: confirmedCount + 1,
        draft: {
          labelBase64: cleared.labelBase64,
          labelMediaType: cleared.labelMediaType,
          month: cleared.month,
          year: cleared.year,
          apiStatus: cleared.apiStatus,
          name: cleared.name,
          strength: cleared.strength,
          dosageForm: cleared.dosageForm,
          unitType: cleared.unitType,
          quantity: resolveQuantity(null),
          quantityBuffer: null,
          attentionName: cleared.attentionName,
          attentionStrength: cleared.attentionStrength,
          attentionForm: cleared.attentionForm,
          unit: cleared.unit,
          matchStatus: cleared.matchStatus,
          suggestedName: cleared.suggestedName,
          aiParsedName: cleared.aiParsedName,
        },
        sessionLog: nextLog,
        outboundPending,
      });
    }

    void (async () => {
      try {
        const saved = await onSaveItem?.(payload);
        if (!mountedRef.current) return;
        if (saved === false || saved == null) throw new Error("Could not save item");
        if (saved && saved._batchSaveDeferred) {
          setDeferSaveToast(true);
          window.setTimeout(() => {
            if (mountedRef.current) setDeferSaveToast(false);
          }, 4200);
        }
        const savedId =
          saved && typeof saved === "object" && "id" in saved ? saved.id : payload.id;
        lastSavedRef.current = { id: savedId, name: displayLine };
        outboundPendingRef.current.delete(payload.id);
        window.setTimeout(() => recoveryFlushRef.current(), 0);
      } catch (e) {
        if (!mountedRef.current) return;
        outboundPendingRef.current.delete(payload.id);
        setSaveError(humanizeNetworkOrUnknown(e));
        setSaveErrorItemId(payload.id);
        failedPayloadsRef.current.set(payload.id, { payload, line: displayLine, attempts: 1 });
        setFailedItems((prev) => [...prev, { id: payload.id, line: displayLine }]);
        setSessionLog((prev) => {
          const next = prev.filter((row) => row.key !== payload.id);
          sessionLogRef.current = next;
          return next;
        });
        setConfirmedCount((c) => Math.max(0, c - 1));
        setItemIndex((x) => Math.max(1, x - 1));
        window.setTimeout(() => recoveryFlushRef.current(), 0);
      }
    })();
  }, [draft, quantityDigits, onSaveItem, parseAccessToken, userId, itemIndex, confirmedCount, step]);

  /** Resends a failed item's already-built payload — no need to re-enter or re-scan anything. */
  const retryFailedItem = useCallback(
    (id) => {
      const entry = failedPayloadsRef.current.get(id);
      if (!entry) return;
      setSaveError(null);
      void (async () => {
        try {
          const saved = await onSaveItem?.(entry.payload);
          if (!mountedRef.current) return;
          if (saved === false || saved == null) throw new Error("Could not save item");
          if (saved && saved._batchSaveDeferred) {
            setDeferSaveToast(true);
            window.setTimeout(() => {
              if (mountedRef.current) setDeferSaveToast(false);
            }, 4200);
          }
          failedPayloadsRef.current.delete(id);
          setFailedItems((prev) => prev.filter((row) => row.id !== id));
          setSaveErrorItemId((cur) => (cur === id ? null : cur));
          const nextLog = [
            ...sessionLogRef.current,
            { key: id, line: entry.line, quantity: entry.payload.quantity },
          ];
          sessionLogRef.current = nextLog;
          setSessionLog(nextLog);
          setConfirmedCount((c) => c + 1);
        } catch (e) {
          if (!mountedRef.current) return;
          entry.attempts += 1;
          setSaveError(humanizeNetworkOrUnknown(e));
          setSaveErrorItemId(id);
        }
      })();
    },
    [onSaveItem],
  );

  /** Stops retrying a failed item — it stays listed on the summary screen as not saved. */
  const skipFailedItem = useCallback((id) => {
    setSaveError(null);
    setSaveErrorItemId((cur) => (cur === id ? null : cur));
  }, []);

  const onKeypadDigit = useCallback((d) => {
    // Read + flip the ref here, outside the state updater: React may invoke a
    // functional setState updater more than once per commit (e.g. Strict Mode's
    // double-invoke check), and mutating a ref from inside that updater is not
    // idempotent — the second invocation would observe the ref already flipped
    // to `true` by the first, wrongly treating it as "already typing".
    const wasTyping = quantityTypingRef.current;
    quantityTypingRef.current = true;
    setQuantityDigits((prev) => {
      // First digit after a fresh state OR after a +1 tap starts a brand-new number
      // (e.g. tap to 4, then type "1" "0" → 10, not 41).
      const base = wasTyping && prev !== null ? prev : "";
      if (base.length >= 3) return base;
      return base + d;
    });
  }, []);

  const onKeypadClear = useCallback(() => {
    quantityTypingRef.current = true;
    setQuantityDigits("");
  }, []);

  const onKeypadBackspace = useCallback(() => {
    quantityTypingRef.current = true;
    setQuantityDigits((prev) => {
      if (prev === null) return "";
      if (prev.length <= 1) return "";
      return prev.slice(0, -1);
    });
  }, []);

  /** Tapping the quantity number itself bumps it by 1 — it is not a text field to edit. */
  const incrementQuantity = useCallback(() => {
    hapticLight();
    quantityTypingRef.current = false;
    setQuantityDigits((prev) => String(Math.min(999, resolveQuantity(prev) + 1)));
  }, []);

  const transitionMs = 100;
  const stepOffset = step === STEP_CAPTURE ? 0 : step === STEP_DATE ? -100 : -200;
  const slideWrapStyle = {
    width: "300%",
    display: "flex",
    height: "100%",
    transform: `translateX(${stepOffset / 3}%)`,
    transition: `transform ${transitionMs}ms cubic-bezier(0.22, 0.7, 0.2, 1)`,
  };

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const startLongPress = useCallback(
    (fn) => {
      clearLongPress();
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTimerRef.current = null;
        fn();
      }, LONG_PRESS_MS);
    },
    [clearLongPress],
  );

  const openEdit = useCallback((field) => {
    setEditingField(field);
    const focusField = () => {
      if (field === "name") {
        nameInputRef.current?.focus();
        nameInputRef.current?.select?.();
      } else if (field === "strength") {
        strengthInputRef.current?.focus();
        strengthInputRef.current?.select?.();
      } else if (field === "form") {
        formInputRef.current?.focus();
        formInputRef.current?.select?.();
      }
    };
    // Double rAF: more reliable keyboard / focus on mobile WebKit after layout.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(focusField);
    });
  }, []);

  const closeEdit = useCallback(() => setEditingField(null), []);

  const finishExit = useCallback(() => {
    void (async () => {
      releaseWakeLock();
      if (userId) await clearBatchScanRecovery(userId);
      if (onDone) onDone();
      else onCancel?.();
    })();
  }, [userId, onDone, onCancel, releaseWakeLock]);

  const handleDone = () => {
    setSessionUi("summary");
  };

  const formLineDisplay = [draft.dosageForm, draft.unitType].filter((x) => String(x || "").trim()).join(" — ");
  const expiryLabel = formatBatchScanExpiryLabel(draft.month, draft.year);

  return (
    <div
      className="batch-scan-ui"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "#000",
        fontFamily: font,
        viewTransitionName: "ed-app-surface",
      }}
    >
      {sessionUi === "summary" ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 80,
            background: "linear-gradient(180deg, #0a1628 0%, #0c1118 100%)",
            color: "#e8f4fc",
            display: "flex",
            flexDirection: "column",
            padding: "max(16px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom))",
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap", marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => router.push("/dashboard")}
              style={{
                padding: "6px 10px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.06)",
                color: "#e2e8f0",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: font,
              }}
            >
              ← Back
            </button>
            <h1 style={{ margin: 0, fontSize: "1.35rem", fontWeight: 800, letterSpacing: "-0.03em" }}>
              Batch session
            </h1>
          </div>
          <p style={{ margin: "0 0 16px", color: "rgba(232,244,252,0.65)", fontSize: "0.95rem", lineHeight: 1.5 }}>
            {confirmedCount} item{confirmedCount === 1 ? "" : "s"} scanned · Total quantity{" "}
            {sessionLog.reduce((a, x) => a + x.quantity, 0)}
            {hasActiveUnconfirmed ? (
              <span style={{ display: "block", marginTop: 8, color: "rgba(251, 191, 36, 0.95)" }}>
                You have an item in progress that was not saved.
              </span>
            ) : null}
          </p>
          {failedItems.length > 0 ? (
            <div
              style={{
                margin: "0 0 16px",
                padding: "12px 14px",
                borderRadius: 14,
                background: "rgba(127, 29, 29, 0.35)",
                border: "1px solid rgba(248, 113, 113, 0.4)",
              }}
              role="alert"
            >
              <div style={{ color: "#fecaca", fontSize: "0.85rem", fontWeight: 700, marginBottom: 8 }}>
                {failedItems.length} item{failedItems.length === 1 ? "" : "s"} failed to save to dashboard
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {failedItems.map((row) => (
                  <li key={`failed-line-${row.id}`}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        fontSize: "0.85rem",
                        color: "#fecaca",
                      }}
                    >
                      <span>{row.line}</span>
                      {failedPayloadsRef.current.has(row.id) ? (
                        <button
                          type="button"
                          onClick={() => retryFailedItem(row.id)}
                          style={{
                            flexShrink: 0,
                            padding: "5px 10px",
                            borderRadius: 8,
                            border: "1px solid rgba(255,255,255,0.35)",
                            background: "rgba(255,255,255,0.14)",
                            color: "#fee2e2",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            cursor: "pointer",
                            fontFamily: font,
                          }}
                        >
                          Retry
                        </button>
                      ) : null}
                    </div>
                    {saveErrorItemId === row.id && saveError ? (
                      <div style={{ marginTop: 4, fontSize: "0.74rem", color: "#fda4af" }}>{saveError}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div style={{ flex: 1, overflowY: "auto", marginBottom: 16, WebkitOverflowScrolling: "touch" }}>
            {sessionLog.length === 0 ? (
              <div style={{ color: "rgba(232,244,252,0.45)", fontSize: "0.9rem" }}>No lines saved in this session.</div>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {sessionLog.map((row) => (
                  <li
                    key={`session-line-${row.key}`}
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      borderRadius: 14,
                      padding: "12px 14px",
                      border: "1px solid rgba(255,255,255,0.08)",
                      fontSize: "0.88rem",
                      lineHeight: 1.45,
                    }}
                  >
                    <strong style={{ color: "#7dd3fc" }}>×{row.quantity}</strong>{" "}
                    <span style={{ color: "#f5f5f7" }}>{row.line}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="ed-scan-primary"
            onClick={finishExit}
            style={{ width: "100%", padding: "14px 18px", fontSize: "1rem", fontWeight: 700, borderRadius: 14 }}
          >
            Go to Dashboard
          </button>
        </div>
      ) : null}

      {sessionUi === "scanner" && step === STEP_CAPTURE ? null : (
        <PharmacyWarehouseBackdrop position="absolute" />
      )}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 2,
          opacity: step === STEP_CAPTURE ? 1 : 0,
          pointerEvents: step === STEP_CAPTURE ? "auto" : "none",
          transition: `opacity ${transitionMs}ms ease`,
        }}
      />

      {step !== STEP_CAPTURE ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 4,
            background: "#0a0e14",
            pointerEvents: "none",
          }}
        />
      ) : null}

      {sessionUi === "scanner" && step === STEP_CAPTURE ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, transparent 40%, rgba(0,0,0,0.35) 100%)",
            pointerEvents: "none",
          }}
        />
      ) : null}

      {flash ? (
        <div style={{ position: "absolute", inset: 0, zIndex: 25, background: "rgba(255,255,255,0.18)", pointerEvents: "none" }} />
      ) : null}
      {checkFlash ? (
        <div
          className="ed-batch-check-flash"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            background: "rgba(34, 197, 94, 0.12)",
          }}
        >
          <span style={{ fontSize: 64, color: "#4ade80", filter: "drop-shadow(0 4px 20px rgba(74,222,128,0.5))" }}>✓</span>
        </div>
      ) : null}

      {deferSaveToast ? (
        <div
          style={{
            position: "absolute",
            top: "max(12px, env(safe-area-inset-top))",
            left: 16,
            right: 16,
            zIndex: 70,
            background: "rgba(127, 29, 29, 0.92)",
            color: "#fecaca",
            padding: "10px 14px",
            borderRadius: 12,
            fontSize: "0.88rem",
            fontWeight: 600,
            textAlign: "center",
            border: "1px solid rgba(248, 113, 113, 0.4)",
          }}
        >
          Saved locally — will sync shortly.
        </div>
      ) : null}

      {sessionUi === "scanner" ? (
        <div
          style={{
            position: "relative",
            zIndex: 10,
            padding: "max(12px, env(safe-area-inset-top)) 16px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", minWidth: 0, flex: 1 }}>
            <button
              type="button"
              onClick={goBackStep}
              style={{
                flexShrink: 0,
                padding: "6px 10px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.22)",
                background: "rgba(255,255,255,0.07)",
                color: "#e2e8f0",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: font,
              }}
            >
              ← Back
            </button>
            <div style={{ color: "#f5f5f7", fontSize: "0.95rem", fontWeight: 700 }}>Item {itemIndex}</div>
          </div>
          <button type="button" onClick={handleDone} className="ed-scan-done" aria-label="Done">
            Done
          </button>
        </div>
      ) : null}

      {sessionUi === "scanner" && step === STEP_CAPTURE && saveError ? (
        <div
          style={{
            position: "relative",
            zIndex: 12,
            margin: "0 16px 8px",
            padding: "10px 12px",
            borderRadius: 12,
            background: "rgba(127, 29, 29, 0.92)",
            color: "#fecaca",
            fontSize: "0.82rem",
            fontWeight: 600,
            lineHeight: 1.4,
            border: "1px solid rgba(248, 113, 113, 0.45)",
          }}
          role="alert"
        >
          {(() => {
            const entry = saveErrorItemId != null ? failedPayloadsRef.current.get(saveErrorItemId) : null;
            const stillFailing = entry && entry.attempts > 1;
            return (
              <>
                {entry ? <div style={{ marginBottom: 4, fontWeight: 700 }}>{entry.line} didn’t save</div> : null}
                {saveError}
                {stillFailing ? (
                  <div style={{ marginTop: 4 }}>You can keep trying, or skip it and add it manually later.</div>
                ) : null}
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {entry ? (
                    <button
                      type="button"
                      onClick={() => retryFailedItem(saveErrorItemId)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid rgba(255,255,255,0.35)",
                        background: "rgba(255,255,255,0.14)",
                        color: "#fee2e2",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      Retry
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => (entry ? skipFailedItem(saveErrorItemId) : setSaveError(null))}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.35)",
                      background: "rgba(0,0,0,0.2)",
                      color: "#fee2e2",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: font,
                    }}
                  >
                    {entry ? "Skip item" : "Dismiss"}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      ) : null}

      {sessionUi === "scanner" ? (
        <div style={{ position: "absolute", inset: 0, top: 52, zIndex: 10, overflow: "hidden" }}>
          <div className="ed-batch-slide-track" style={slideWrapStyle}>
            {/* STEP 1 */}
            <div
              style={{
                width: "33.3333%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                alignItems: "center",
                paddingBottom: "max(28px, env(safe-area-inset-bottom))",
              }}
            >
              {!cameraError ? (
                <button
                  type="button"
                  onClick={handleCapture}
                  disabled={!cameraReady}
                  className="ed-scan-shutter"
                  aria-label="Capture"
                  style={{
                    marginBottom: 8,
                    width: 88,
                    height: 88,
                  }}
                >
                  <span className="ed-scan-shutter-inner" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="ed-scan-fallback-primary"
                  style={{ marginBottom: 24 }}
                >
                  Choose photo
                </button>
              )}
              {uxProfile.allowPictureInPicture && !cameraError ? (
                <button
                  type="button"
                  onClick={() => void togglePictureInPicture()}
                  disabled={!cameraReady}
                  className="ed-scan-ghost"
                  style={{ fontSize: "0.75rem", marginBottom: 8, opacity: 0.7 }}
                >
                  {pipActive ? "Close mini camera" : "Mini camera"}
                </button>
              ) : null}
              {barcodeSupported ? (
                <button
                  type="button"
                  onClick={() => barcodeFileRef.current?.click()}
                  className="ed-scan-ghost"
                  style={{ fontSize: "0.75rem", marginTop: 10, opacity: 0.82 }}
                >
                  Scan barcode image
                </button>
              ) : null}
            </div>

            {/* STEP 2 */}
            <div
              style={{
                width: "33.3333%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                padding: "8px 16px max(12px, env(safe-area-inset-bottom))",
                boxSizing: "border-box",
                color: "#e8f4fc",
              }}
            >
              <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {draft.apiStatus === PARSE_LOADING ? (
                  <div style={{ marginBottom: 12 }} aria-live="polite">
                    <div className="ed-batch-ai-pulse" />
                    <div style={{ textAlign: "center", marginTop: 8, fontSize: "0.78rem", color: "rgba(232,244,252,0.55)" }}>
                      Reading label…
                    </div>
                  </div>
                ) : aiParseWarn ? (
                  <div
                    role="status"
                    style={{
                      marginBottom: 12,
                      padding: "10px 12px",
                      borderRadius: 12,
                      background: "rgba(251, 191, 36, 0.12)",
                      border: "1px solid rgba(251, 191, 36, 0.35)",
                      fontSize: "0.78rem",
                      lineHeight: 1.45,
                      color: "rgba(254, 243, 199, 0.95)",
                    }}
                  >
                    {aiParseWarn} You can fill the fields manually on the next step.
                  </div>
                ) : (
                  <div style={{ height: 20, marginBottom: 12 }} />
                )}
                <div style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: 12, letterSpacing: "-0.02em" }}>
                  Set expiry date
                </div>
                <div style={{ fontSize: "0.78rem", color: "rgba(232,244,252,0.55)", marginBottom: 12 }}>
                  Tap a month and a year — you&rsquo;ll move on automatically.
                </div>
                <MonthYearGridPicker
                  month={draft.month}
                  year={draft.year}
                  years={years}
                  onPickMonth={pickMonth}
                  onPickYear={pickYear}
                />
              </div>
            </div>

            {/* STEP 3 */}
            <div
              style={{
                width: "33.3333%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                padding: "4px 14px 0",
                boxSizing: "border-box",
                color: "#e8f4fc",
              }}
            >
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: 8 }}>
                <div
                  className="ed-batch-confirm-card"
                  aria-busy={draft.apiStatus === PARSE_LOADING}
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    borderRadius: 16,
                    padding: "14px 14px 12px",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {draft.labelBase64 ? (
                    <div
                      style={{
                        marginBottom: 12,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "#0a0e14",
                        maxHeight: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element -- captured label frame */}
                      <img
                        src={`data:${draft.labelMediaType || "image/jpeg"};base64,${draft.labelBase64}`}
                        alt="Captured label"
                        style={{ display: "block", maxWidth: "100%", maxHeight: 140, objectFit: "contain" }}
                      />
                    </div>
                  ) : null}
                  <FieldRow
                    label="Medicine name"
                    attention={draft.attentionName}
                    {...fieldPointerHandlers(
                      draft.apiStatus !== PARSE_LOADING && editingField !== "name",
                      startLongPress,
                      clearLongPress,
                      () => openEdit("name"),
                    )}
                  >
                    {draft.apiStatus === PARSE_LOADING ? (
                      <SkeletonBlock />
                    ) : editingField === "name" ? (
                      <div style={{ position: "relative", width: "100%" }}>
                        <input
                          ref={nameInputRef}
                          value={draft.name}
                          autoComplete="off"
                          aria-autocomplete="list"
                          aria-controls="ed-batch-pos-name-suggest"
                          onPointerDown={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            labelFieldEditedRef.current.name = true;
                            setDraft((d) => ({ ...d, name: e.target.value }));
                          }}
                          onBlur={() => {
                            setDraft((d) => ({
                              ...d,
                              attentionName: d.name.trim() === "" ? d.attentionName : false,
                            }));
                            closeEdit();
                          }}
                          placeholder={
                            posReferenceEntries.length ? "Type to match your POS / sheet list…" : undefined
                          }
                          className="ed-batch-edit-input"
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: "2px solid #2dd4bf",
                            background: "rgba(0,0,0,0.35)",
                            color: "#fff",
                            fontSize: "1rem",
                          }}
                        />
                        {posNameSuggestions.length > 0 ? (
                          <ul
                            id="ed-batch-pos-name-suggest"
                            role="listbox"
                            style={{
                              position: "absolute",
                              left: 0,
                              right: 0,
                              top: "100%",
                              margin: "4px 0 0",
                              padding: "4px 0",
                              maxHeight: "min(40vh, 220px)",
                              overflowY: "auto",
                              listStyle: "none",
                              zIndex: 20,
                              borderRadius: 10,
                              background: "rgba(15, 23, 42, 0.97)",
                              border: "1px solid rgba(45, 212, 191, 0.35)",
                              boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
                            }}
                          >
                            {posNameSuggestions.map((s) => (
                              <li key={`${s.name}-${s.dosage}-${s.unit}`} role="option">
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    applyPosReferencePick(s);
                                  }}
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "10px 12px",
                                    border: "none",
                                    background: "transparent",
                                    color: "#f5f5f7",
                                    fontSize: "0.9rem",
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                  }}
                                >
                                  <span style={{ fontWeight: 650 }}>{s.name}</span>
                                  {s.dosage ? (
                                    <span style={{ color: "rgba(232,244,252,0.65)", fontWeight: 500 }}>
                                      {" "}
                                      · {s.dosage}
                                    </span>
                                  ) : null}
                                  {s.qty != null ? (
                                    <span
                                      style={{
                                        display: "block",
                                        fontSize: "0.72rem",
                                        color: "rgba(148, 197, 255, 0.85)",
                                        marginTop: 2,
                                      }}
                                    >
                                      Stock sheet: {s.qty} {s.unit || ""}
                                    </span>
                                  ) : null}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : (
                      <span style={{ fontSize: "1.02rem", fontWeight: 650 }}>{draft.name || "—"}</span>
                    )}
                  </FieldRow>
                  <FieldRow
                    label="Strength"
                    attention={draft.attentionStrength}
                    {...fieldPointerHandlers(
                      draft.apiStatus !== PARSE_LOADING && editingField !== "strength",
                      startLongPress,
                      clearLongPress,
                      () => openEdit("strength"),
                    )}
                  >
                    {draft.apiStatus === PARSE_LOADING ? (
                      <SkeletonBlock />
                    ) : editingField === "strength" ? (
                      <input
                        ref={strengthInputRef}
                        value={draft.strength}
                        onPointerDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          labelFieldEditedRef.current.strength = true;
                          setDraft((d) => ({ ...d, strength: e.target.value }));
                        }}
                        onBlur={() => {
                          setDraft((d) => ({
                            ...d,
                            attentionStrength: d.strength.trim() === "" ? d.attentionStrength : false,
                          }));
                          closeEdit();
                        }}
                        className="ed-batch-edit-input"
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: "2px solid #2dd4bf",
                          background: "rgba(0,0,0,0.35)",
                          color: "#fff",
                          fontSize: "1rem",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: "0.98rem" }}>{draft.strength || "—"}</span>
                    )}
                  </FieldRow>
                  <FieldRow
                    label="Dosage form · Unit"
                    attention={draft.attentionForm}
                    {...fieldPointerHandlers(
                      draft.apiStatus !== PARSE_LOADING && editingField !== "form",
                      startLongPress,
                      clearLongPress,
                      () => openEdit("form"),
                    )}
                  >
                    {draft.apiStatus === PARSE_LOADING ? (
                      <SkeletonBlock />
                    ) : editingField === "form" ? (
                      <input
                        ref={formInputRef}
                        value={[draft.dosageForm, draft.unitType].filter(Boolean).join(" — ")}
                        onPointerDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          labelFieldEditedRef.current.form = true;
                          const raw = e.target.value;
                          const parts = raw.split(/\s*[—–-]\s*/);
                          setDraft((d) => ({
                            ...d,
                            dosageForm: (parts[0] || "").trim(),
                            unitType: (parts.slice(1).join(" — ") || "").trim(),
                          }));
                        }}
                        onBlur={() => {
                          setDraft((d) => ({
                            ...d,
                            attentionForm:
                              d.dosageForm.trim() === "" || d.unitType.trim() === "" ? d.attentionForm : false,
                          }));
                          closeEdit();
                        }}
                        placeholder="Syrup — Bottle"
                        className="ed-batch-edit-input"
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: "2px solid #2dd4bf",
                          background: "rgba(0,0,0,0.35)",
                          color: "#fff",
                          fontSize: "0.95rem",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: "0.98rem" }}>{formLineDisplay || "—"}</span>
                    )}
                  </FieldRow>
                  <FieldRow
                    label="Expiry date"
                    attention={false}
                    {...fieldPointerHandlers(editingField !== "expiry", startLongPress, clearLongPress, () =>
                      openEdit("expiry"),
                    )}
                  >
                    {editingField === "expiry" ? (
                      <div
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{ display: "flex", flexDirection: "column", gap: 10 }}
                      >
                        <MonthYearGridPicker
                          compact
                          month={draft.month}
                          year={draft.year}
                          years={years}
                          onPickMonth={setMonth}
                          onPickYear={setYear}
                        />
                        <button type="button" className="ed-scan-primary" onClick={closeEdit} style={{ padding: "10px", borderRadius: 12 }}>
                          Done
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontSize: "0.98rem", fontWeight: 600 }}>{expiryLabel || "—"}</span>
                        <span
                          style={{
                            display: "inline-flex",
                            width: "fit-content",
                            alignItems: "center",
                            borderRadius: 999,
                            padding: "3px 9px",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: "0.01em",
                            background: "rgba(245, 158, 11, 0.14)",
                            border: "1px solid rgba(245, 158, 11, 0.42)",
                            color: "rgba(254, 243, 199, 0.95)",
                          }}
                        >
                          End of month expiry (standard)
                        </span>
                      </div>
                    )}
                  </FieldRow>
                </div>
              </div>

              <div
                style={{
                  flexShrink: 0,
                  paddingBottom: `max(8px, env(safe-area-inset-bottom), ${editingField ? kbInset * 0.25 : 0}px)`,
                  transform: editingField && editingField !== "expiry" ? `translateY(-${Math.min(kbInset, 220)}px)` : undefined,
                  transition: "transform 0.2s ease",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "rgba(232,244,252,0.55)",
                    marginBottom: 4,
                  }}
                >
                  How many units expire {expiryLabel ? `in ${expiryLabel}` : "on this date"}?
                </div>
                <button
                  type="button"
                  onClick={incrementQuantity}
                  aria-label="Increase quantity by 1"
                  className="ed-batch-qty-display"
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "center",
                    marginBottom: 10,
                    fontSize: "2.1rem",
                    fontWeight: 800,
                    letterSpacing: "-0.03em",
                    minHeight: 44,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    color: "inherit",
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                >
                  {displayQuantityString(quantityDigits) === "" ? (
                    <span style={{ color: "rgba(232,244,252,0.35)" }}>|</span>
                  ) : (
                    displayQuantityString(quantityDigits)
                  )}
                </button>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.68rem",
                    color: "rgba(232,244,252,0.4)",
                    marginBottom: 10,
                    marginTop: -6,
                  }}
                >
                  Tap the number to +1, or type an exact amount below
                </div>
                <button
                  type="button"
                  onClick={() => confirmItem()}
                  className="ed-scan-primary"
                  aria-label="Send this item to the dashboard"
                  style={{
                    width: "100%",
                    padding: "13px 18px",
                    fontSize: "1rem",
                    fontWeight: 800,
                    borderRadius: 14,
                    marginBottom: 10,
                  }}
                >
                  SEND TO DASHBOARD
                </button>
                {editingField === null ? (
                  <NumericKeypad
                    onDigit={onKeypadDigit}
                    onClear={onKeypadClear}
                    onBackspace={onKeypadBackspace}
                  />
                ) : null}
                {saveError ? (
                  <div style={{ color: "#fecaca", fontSize: "0.85rem", marginTop: 8, textAlign: "center" }}>{saveError}</div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onPickFile} />
      <input ref={barcodeFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => void onPickBarcodeFile(e)} />
    </div>
  );
}

function FieldRow({ label, attention, children, onPointerDown, onPointerUp, onPointerCancel, onPointerLeave }) {
  return (
    <div
      style={{ marginBottom: 14, touchAction: "manipulation", userSelect: "none" }}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerLeave}
    >
      <div style={{ fontSize: "0.68rem", color: "rgba(232,244,252,0.5)", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
        {attention ? (
          <span style={{ width: 6, height: 6, borderRadius: 99, background: "rgba(245, 158, 11, 0.95)", flexShrink: 0 }} />
        ) : null}
        {label}
      </div>
      <div style={attention ? { borderBottom: "2px solid rgba(245, 158, 11, 0.55)", paddingBottom: 6 } : {}}>{children}</div>
    </div>
  );
}
